/**
 * @module app.pp
 */

import { autoinject, observable, TaskQueue } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { focusFirst } from 'app/shared/lib/focus';
import { AuthExternalSystemChangeEvent, IAuthChangeCustomEventDetail } from 'app/um/lib/events';
import { fireEvents } from 'app/shared/lib/fire-events';
import { guid } from 'app/shared/lib/guid';
import { getReference } from 'app/shared/models/essette-types';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { CwqiManager, ICodes, AutoAuthClosedEvent, AutoAuthCanceledEvent } from 'app/ext/cwqi/cwqi-manager';
import { AuthService } from 'app/um/services/auth-service';
import { NoteService } from 'app/core/services/note-service';
import { LookupService } from 'app/cd/services/lookup-service';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { getFocusSelector, IReturnFocus, returnFocus } from 'app/shared/lib/focus';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';

export enum DocumentOptions {
    Service = 'Service',
    Diagnosis = 'Diagnosis',
    None = ''
}
@autoinject()
export class AutoAuthStep extends ComponentBase implements IWizardStep {

    ////****************** Fields and Properties ************************************////
    @bindable
    protected auth: UM.Auth | null = null;

    @bindable.boolean()
    protected readonly: boolean = false;

    @bindable.boolean()
    protected pastStep: boolean = false;

    public canSkip: boolean = false;
    public canContinue: boolean = false;
    public canPerformFinalSubmit: boolean = false;

    protected primaryServiceCode: ICodes | null = null;
    protected serviceCodes: ICodes[] = [];
    protected primaryDiagnosisCode: ICodes | null = null;
    protected diagnosisCodes: ICodes[] = [];
    protected diagTypes: ReadonlyArray<CD.DiagnosisType> = [];
    protected serviceTypes: ReadonlyArray<CD.DiagnosisType> = [];
    protected isComplete: boolean = false;
    protected enableAutoAuth: boolean = true;
    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        moduleCode: 'UM',
        excludeSystem: false
    }
    protected areCodesAvailableToSend: boolean = false;
    private noteCategory: Core.NoteCategoryWithSubCategoryModel | null = null;
    private eventSubscriptions: Array<Subscription> = [];
    protected missingPrimaryCode: boolean = true;

    @observable
    private selectedDocumentOption: string = DocumentOptions.None;

    protected id: guid = guid();
    protected activeElementPath: IReturnFocus | null = null;

    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly cwqi: CwqiManager,
        protected readonly authService: AuthService,
        private readonly noteService: NoteService,
        private readonly lookupService: LookupService,
        private readonly events: EventAggregator,
        protected readonly taskQueue: TaskQueue,
        private $element: Element,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    //protected created(owningView: View, myView: View): void { }
    protected bind(): void {
        this.loadDiagnosisTypes();
        this.loadServiceTypes();
        this.noteService.configure(this.notesConfig);
        this.loadNoteCategory();
        this.availableCodesToSend();
        this.isComplete = !!this.auth?.externalSystem?.externalResultsIdentifier || !this.areCodesAvailableToSend;
        this.enableAutoAuth = this.auth?.diagnoses?.length !== 0 || this.auth.services?.length !== 0 ? true : false;
        this.canContinue = this.isComplete || !this.enableAutoAuth;


        this.eventSubscriptions.push(
            this.events.subscribe(AuthExternalSystemChangeEvent, () => { this.triggerReload(); }),
            this.events.subscribe(AutoAuthClosedEvent, () => { this.canSkip = false; }),
            this.events.subscribe(AutoAuthCanceledEvent, () => { this.canSkip = true; })
        );

    }

    protected unbind() {
        this.eventSubscriptions.forEach((s) => s.dispose());
        this.eventSubscriptions = [];
    }

    ////****************** Events ***************************************************////
    protected selectedDocumentOptionChanged(newValue: string) {
        let documentService: boolean = false;
        let documentDiagnosis: boolean = false;

        switch(newValue) {
            case DocumentOptions.Service:
                documentService = true;
                // Check if primary code is already set for services
                this.missingPrimaryCode = this.serviceCodes.findIndex(c => c.isPrimary) === -1;
                break;
            case DocumentOptions.Diagnosis:
                documentDiagnosis = true;
                // Check if primary code is already set for diagnoses
                this.missingPrimaryCode = this.diagnosisCodes.findIndex(c => c.isPrimary) === -1;
                break;
        }

        this.serviceCodes.forEach(code => code.isPreCert = documentService);
        this.diagnosisCodes.forEach(code => code.isPreCert = documentDiagnosis);
    }

    ////****************** Functions ************************************************////
    public continueStep(): void { }
    public openStep(): void {
        this.availableCodesToSend();
        this.isComplete = !!this.auth?.externalSystem?.externalResultsIdentifier || !this.areCodesAvailableToSend;
        this.canContinue = this.isComplete || !this.enableAutoAuth;
        if(!this.readonly && this.enableAutoAuth && !this.canContinue)
            setTimeout(() => focusFirst(this.$element), 250); //first option
    }
    public async closeStep(): Promise<void> { }
    public async finalSubmit(): Promise<void> { }

    protected authChanged() {
        this.loadCodes();
        this.availableCodesToSend();

        this.isComplete = !!this.auth?.externalSystem?.externalResultsIdentifier || !this.areCodesAvailableToSend;
        this.enableAutoAuth = !this.auth?.externalSystem?.externalResultsIdentifier;
        this.canContinue = this.isComplete || !this.enableAutoAuth;

    }

    // Set a combined array of Diagnosis and Service codes
    protected loadCodes() {
        this.serviceCodes = [];
        this.diagnosisCodes = [];

        this.auth?.diagnoses?.forEach(d => {
            // Skip duplicate diagnoses
            if(this.diagnosisCodes.findIndex(c => c.code === d.definition?.code) === -1) {
                const code: ICodes = {
                    modelCode: 'CD.Diagnosis',
                    isPreCert: false,
                    isPrimary: !!d.isPrimary,
                    code: d.definition?.code!,
                    description: d.definition?.description!,
                    type: this.diagTypes.find(t => t.id == d.definition?.type!.id)?.mappings?.find(m => m.externalSystem!.code == 'CWQI')?.code! || 'ICD10CM',
                    determination: undefined
                };
                this.diagnosisCodes.push(code);
            }
        });

        this.auth?.services?.forEach(s => {
            // Skip duplicate services
            if(this.serviceCodes.findIndex(c => c.code === s.definition?.code) === -1) {
                const code: ICodes = {
                    modelCode: 'CD.Service',
                    isPreCert: false,
                    isPrimary: !!s.isPrimary && !s.status?.description, //if we have a status, we shouldn't mark it as primary
                    code: s.definition?.code!,
                    description: s.definition?.description!,
                    type: this.serviceTypes.find(t => t.id == s.definition?.type!.id)?.mappings?.find(m => m.externalSystem!.code == 'CWQI')?.code! || 'CPT',
                    qty: s.quantity?.requested || 0,
                    determination: s.status?.determination?.isDetermination && s.status.determination.status?.description ? s.status.determination.status.description : undefined
                };
                this.serviceCodes.push(code);
            }
        });

        //the primary code sent to AA isn't stored in the DB anywhere, so storing it locally so we can put it back when we return from AA
        if(this.primaryServiceCode?.code) this.serviceCodes.forEach(c => { if(c.code === this.primaryServiceCode?.code) { c.isPrimary = true; } else { c.isPrimary = false; } });
        if(this.primaryDiagnosisCode?.code) this.diagnosisCodes.forEach(c => { if(c.code === this.primaryDiagnosisCode?.code) { c.isPrimary = true; } else { c.isPrimary = false; } });

        this.primaryServiceCode = this.serviceCodes.find(c => c.isPrimary) || null;
        this.primaryDiagnosisCode = this.diagnosisCodes.find(c => c.isPrimary) || null;

        //create a new array to bubble up the codes marked as primary
        const sortedServiceCodes: ICodes[] = [... this.serviceCodes.filter(c => c.isPrimary),
        ...this.serviceCodes.filter(c => !c.isPrimary)];
        const sortedDiagnosisCodes: ICodes[] = [... this.diagnosisCodes.filter(c => c.isPrimary),
        ...this.diagnosisCodes.filter(c => !c.isPrimary)];

        //replace the array with our sorted codes
        this.serviceCodes = sortedServiceCodes;
        this.diagnosisCodes = sortedDiagnosisCodes;

        const previousSelectedDocumentOption = this.selectedDocumentOption;
        if(this.serviceCodes.length) {
            this.selectedDocumentOption = DocumentOptions.Service;
        }
        else if(this.diagnosisCodes.length) {
            this.selectedDocumentOption = DocumentOptions.Diagnosis;
        }
        else {
            this.selectedDocumentOption = DocumentOptions.None;
        }

        //call this method even if the selected option hasn't changed, so we can set the isPreCert flags correctly
        if(previousSelectedDocumentOption === this.selectedDocumentOption) {
            this.selectedDocumentOptionChanged(this.selectedDocumentOption);
        }

        //check whether or not something is marked as primary
        this.missingPrimaryCode = this.serviceCodes.findIndex(c => c.isPreCert && c.isPrimary) === -1 && this.diagnosisCodes.findIndex(c => c.isPreCert && c.isPrimary) === -1;
    }

    protected availableCodesToSend(): void {
        //At least 1 diagnosis and NO service codes, AA should be enabled
        if(this.auth?.services?.length === 0 && this.auth?.diagnoses?.length || 0 > 0) {
            this.areCodesAvailableToSend = true;
            return;
        }

        //every service has a determination, AA can be skipped
        this.areCodesAvailableToSend = !this.auth?.services?.every(s => s.status?.determination?.status?.determination?.isDetermination);
    }

    protected openAutoAuth(): void {
        if(!this.isComplete && this.auth) {
            const codes = this.serviceCodes.concat(this.diagnosisCodes);
            this.cwqi.openAutoAuth(this.auth, codes);

            this.canSkip = true;
        }
    }

    public skipStep(): void {
        //Store info about the "skip" event
        this.saveNote();
    }

    protected triggerReload(): void {
        if(this.$element && this.auth) {
            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: true }
            fireEvents(this.$element, ['auth-change'], detail);
        }
    }

    // Save a Note to the Auth indicating this step was skipped by the user
    private async saveNote(): Promise<boolean> {
        try {
            const note = this.buildNote();

            await this.insertNote(note);

            return true;
        }
        catch(error) {
            this.logger.error('Error Saving Note', error);
            return false;
        }
    }

    private buildNote(): UM.AuthNote {
        const userDisplayName = this.env.user.name.display + ' (' + this.env.user.loginName + ')';

        const note: UM.AuthNote = {
            category: getReference(this.noteCategory, { subCategory: getReference(this.noteCategory?.subCategory) || undefined }),
            text: 'The user ' + userDisplayName + ' skipped documenting medical necessity.',
            auth: {
                id: this.auth?.id
            }
        };

        return note;
    }

    protected setPrimary(code: ICodes, documentOption: DocumentOptions) {
        this.activeElementPath = getFocusSelector(document.activeElement as HTMLElement);
        if(documentOption === DocumentOptions.Service) {
            this.serviceCodes.forEach(c => { if(c.code === code.code) { c.isPrimary = true; } else { c.isPrimary = false; } });
            // If documenting services, primary code will be either missing or not missing based on toggle.
            if(this.selectedDocumentOption === DocumentOptions.Service) {
                this.missingPrimaryCode = false;
            }
            this.primaryServiceCode = code;
        } else if(documentOption === DocumentOptions.Diagnosis) {
            this.diagnosisCodes.forEach(c => { if(c.code === code.code) { c.isPrimary = true; } else { c.isPrimary = false; } });
            // If documenting diagnoses, primary code will be either missing or not missing based on toggle.
            if(this.selectedDocumentOption === DocumentOptions.Diagnosis) {
                this.missingPrimaryCode = false;
            }
            this.primaryDiagnosisCode = code;
        }

        this.taskQueue.queueTask(() => {
            if(this.activeElementPath) {
                returnFocus(this.activeElementPath);
                this.activeElementPath = null;
            }
        })
    }

    protected clearPrimary(documentOption: DocumentOptions): void {
        this.activeElementPath = getFocusSelector(document.activeElement as HTMLElement);

        if(documentOption === DocumentOptions.Service) {
            this.primaryServiceCode = null;
            this.serviceCodes.forEach(c => { c.isPrimary = false; });
        } else if(documentOption === DocumentOptions.Diagnosis) {
            this.primaryDiagnosisCode = null;
            this.diagnosisCodes.forEach(c => { c.isPrimary = false; });
        }

        // If documenting services/diagnoses, primary code will be missing based on services/diagnoses toggle.
        if(this.selectedDocumentOption === DocumentOptions.Service && !this.primaryServiceCode) {
            this.missingPrimaryCode = true;
        } else if(this.selectedDocumentOption === DocumentOptions.Diagnosis && !this.primaryDiagnosisCode) {
            this.missingPrimaryCode = true;
        }

        this.taskQueue.queueTask(() => {
            if(this.activeElementPath) {
                returnFocus(this.activeElementPath);
                this.activeElementPath = null;
            }
        })
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadDiagnosisTypes() {
        try {
            this.state.beginLoad();

            const response = await this.lookupService.loadDiagnosisTypes();
            this.diagTypes = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadServiceTypes() {
        try {
            this.state.beginLoad();

            const response = await this.lookupService.loadServiceTypes();
            this.serviceTypes = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadNoteCategory(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.noteService.loadCategoryByCode('ESSETTE:PP.UM.Auth.Create', {}, false);
            if(response.results.length > 0) {
                this.noteCategory = response.results[0];
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async insertNote(note: UM.AuthNote): Promise<void> {
        try {
            this.state.beginSave();
            const response = await this.noteService.saveNote(note);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
